import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Bot, Zap, Users, Phone, Loader, Clock, Code, TrendingUp, ArrowRight, X, Menu, Shield, Cookie, CheckCircle2, ClipboardList, CreditCard } from 'lucide-react';
import { updateMetaTags, generatePageSchema, generateFAQSchema, injectSchema, setRobotsMeta } from './utils/seoHelpers';

// --- Configuration & Data ---

// Navigation items used for routing and rendering
const navItems = [
  { name: 'Home', page: 'home', icon: Bot },
  { name: 'Solutions', page: 'services', icon: Zap }, // Changed from Expertise to Solutions
  { name: 'About Us', page: 'about', icon: Users },
  { name: 'Contact', page: 'contact', icon: Phone },
];

// Content structured into four service tiers
const servicePillars = [
    {
        name: 'Tier 0 — Free Assessment',
        description: 'AI Automation & Security Assessment',
        icon: Zap,
        services: [
            { name: 'Free Assessment Report', detail: 'A personalized survey about your operations, systems, and pain points. Get a detailed report identifying key automation opportunities.' },
            { name: 'What You Get', detail: 'Summary of your current state, key automation opportunities, potential impact per opportunity, security observations, and next steps.' },
            { name: 'Time & Cost', detail: 'About 10 minutes for the form. Free. We show value upfront so you can see why the next step matters.' },
        ]
    },
    {
        name: 'Tier 1 — Strategy Call',
        description: 'Define Your Roadmap',
        icon: Phone,
        services: [
            { name: '1-on-1 Consultation', detail: 'A dedicated call with Spencer to review your assessment, dig into priorities, and sketch a roadmap forward.' },
            { name: 'What Happens', detail: 'Review your report, deep dive into your biggest pain point, assess feasibility with your current tools, define scope for a potential build.' },
            { name: 'Format & Length', detail: 'Zoom or in-person. 60 minutes. Learn whether DIY guidance or a done-for-you build is right for you.' },
        ]
    },
    {
        name: 'Tier 2 — Done-For-You Build',
        description: 'Custom Automation + Security Implementation',
        icon: Code,
        services: [
            { name: 'Full Build Service', detail: 'We design and build your automation from the ground up—integrations, workflows, security, everything.' },
            { name: 'What You Get', detail: 'Fully designed, tested, deployed automation. Secure integrations. Documentation + training. Security review included. Revision window to get it perfect.' },
            { name: 'How We Work', detail: 'We scope the work, estimate value, and quote a fixed project fee. You pay for results, not our speed.' },
        ]
    },
    {
        name: 'Tier 3 — Managed Retainer',
        description: 'Ongoing Automation + Security Partnership',
        icon: Shield,
        services: [
            { name: 'Monitoring & Maintenance', detail: 'Ongoing monitoring and fixes when tools change or break. APIs evolve, things fail—we keep it running.' },
            { name: 'Security Oversight', detail: 'Continuous security oversight of integrations and access. Your data protection stays a priority.' },
            { name: 'Growth & Support', detail: 'Monthly hours for new automations, optimizations, tweaks. Priority support and regular check-ins.' },
        ]
    }
];

// Tiles shown on the Onboarding page
const onboardingTiles = [
    { title: 'Submit Project Requirements', icon: ClipboardList, to: '/onboarding/submit_requirements' },
    { title: 'Payments', icon: CreditCard },
];

// FAQ content shown on the Solutions page and mirrored into FAQPage schema
const faqItems = [
    { question: 'How much does this cost?', answer: 'The initial Automation & Security Assessment is free. From there, Done-For-You builds are quoted as a fixed project fee based on scope, and ongoing support is a monthly retainer - you always know the number before you commit.' },
    { question: 'Who is this actually for?', answer: 'Service and trade businesses - electricians, plumbers, HVAC contractors, and general contractors - that have outgrown manual, spreadsheet-and-email operations but don\'t need (or can\'t afford) an enterprise consulting firm.' },
    { question: 'Do I need to know anything technical to work with you?', answer: 'No. The assessment and strategy call are conducted in plain language about your operations and pain points. Technical decisions and implementation are handled for you.' },
    { question: 'How is security handled?', answer: 'Access control, data encryption, and secure integrations are designed in from the start of every build, not bolted on afterward - and every engagement includes a security review.' },
];

// --- Tracking Configuration ---
const FACEBOOK_PIXEL_ID = process.env.REACT_APP_FACEBOOK_PIXEL_ID || '1383097013687341';
const GOOGLE_ANALYTICS_ID = process.env.REACT_APP_GOOGLE_ANALYTICS_ID || 'G-ZFJL9B1YWF';

// Cookie utilities
const CookieManager = {
  getCookiePreferences: () => {
    const stored = localStorage.getItem('cookiePreferences');
    return stored ? JSON.parse(stored) : null;
  },
  setCookiePreferences: (preferences) => {
    localStorage.setItem('cookiePreferences', JSON.stringify(preferences));
  },
  getHasConsented: () => {
    const prefs = CookieManager.getCookiePreferences();
    return prefs && (prefs.analytics || prefs.marketing);
  }
};

// Initialize Facebook Pixel using Meta's official implementation
const initializeFacebookPixel = () => {
  if (window.fbq) return;

  // Meta Pixel initialization
  (function(f,b,e,v,n,t,s){
    if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)
  })(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', FACEBOOK_PIXEL_ID);
  window.fbq('track', 'PageView');
};

// Track event with Facebook Pixel. `eventId` lets a matching server-side
// Conversions API call (see api/tracking.php) deduplicate against this one.
const trackFacebookEvent = (eventName, data = {}, eventId = null) => {
  if (CookieManager.getCookiePreferences()?.marketing && window.fbq) {
    if (eventId) {
      window.fbq('track', eventName, data, { eventID: eventId });
    } else {
      window.fbq('track', eventName, data);
    }
  }
};

// Reads the real GA4 client_id out of the _ga cookie set by gtag.js, so a
// server-side Measurement Protocol call (see api/tracking.php) attributes to
// the same client instead of an unrelated anonymous one. Falls back to a
// generated id when the cookie is missing (ad blocker, consent declined).
const getGa4ClientId = () => {
  const match = document.cookie.match(/(?:^|;\s*)_ga=GA\d\.\d\.(\d+\.\d+)/);
  if (match) return match[1];
  return `${Math.round(Math.random() * 1e10)}.${Math.round(Date.now() / 1000)}`;
};

// A random id shared between the client-side pixel/gtag call and the
// matching server-side Conversions API / Measurement Protocol call for a
// given form submission, so the two don't double-count the same conversion.
const generateEventId = () =>
  (window.crypto?.randomUUID?.()) || `evt-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Initialize Google Analytics
const initializeGoogleAnalytics = () => {
  if (window.gtag) return;
  window.dataLayer = window.dataLayer || [];
  function gtag(){window.dataLayer.push(arguments);}
  window.gtag = gtag;
  gtag('js', new Date());
  gtag('config', GOOGLE_ANALYTICS_ID, { 'anonymize_ip': true });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ANALYTICS_ID}`;
  document.head.appendChild(script);
};

// Track event with Google Analytics
const trackGoogleEvent = (eventName, eventData = {}) => {
  if (CookieManager.getCookiePreferences()?.analytics && window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

// Initialize Zoho PageSense. Previously loaded unconditionally from index.html;
// moved here so it respects the same consent gate as GA/Facebook Pixel.
const initializePageSense = () => {
  if (window.__pageSenseLoaded) return;
  window.__pageSenseLoaded = true;
  (function(w, d) {
    const e = d.createElement('script');
    e.type = 'text/javascript';
    e.async = true;
    e.src = 'https://cdn.pagesense.io/js/940483945/d44106efccad4ee08b6b6c949e082b8d.js';
    const x = d.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(e, x);
  })(window, document);
};

// Captures first-touch marketing attribution (UTM params, referrer, landing page)
// once per session and attaches it to lead-form submissions so the CRM record
// shows which channel/campaign actually produced the lead.
const AttributionManager = {
  capture: () => {
    if (sessionStorage.getItem('attribution')) return;

    const params = new URLSearchParams(window.location.search);
    const attribution = {
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      referrer: document.referrer || '',
      landingPage: window.location.pathname,
    };

    sessionStorage.setItem('attribution', JSON.stringify(attribution));
  },
  get: () => {
    try {
      const stored = sessionStorage.getItem('attribution');
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  },
};

// Track page view
const trackPageView = (pageName) => {
  trackFacebookEvent('PageView');
  trackGoogleEvent('page_view', { page_title: pageName });
};

// --- Utility Components ---

/** Cookie Consent Banner */
const CookieConsent = ({ onConsent }) => {
  const [isVisible, setIsVisible] = useState(() => !CookieManager.getHasConsented());
  const [showDetails, setShowDetails] = useState(false);

  const handleAcceptAll = () => {
    const prefs = { analytics: true, marketing: true };
    CookieManager.setCookiePreferences(prefs);
    initializeGoogleAnalytics();
    initializeFacebookPixel();
    initializePageSense();
    setIsVisible(false);
    onConsent?.(prefs);
  };

  const handleRejectAll = () => {
    const prefs = { analytics: false, marketing: false };
    CookieManager.setCookiePreferences(prefs);
    setIsVisible(false);
    onConsent?.(prefs);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-abyss/95 backdrop-blur border-t border-line shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div className="flex items-start gap-4 flex-1">
            <Cookie className="h-6 w-6 text-brand flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-display text-base font-semibold text-ink mb-1">Cookie Preferences</h3>
              <p className="text-mist text-sm mb-2">
                We use cookies to enhance your experience. Some are essential for the site to function, while others help us track analytics and improve our marketing.
              </p>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-brand hover:text-brand-muted text-sm font-medium underline underline-offset-4"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>

              {showDetails && (
                <div className="mt-3 space-y-2 text-sm text-mist bg-panel border border-line p-3 rounded-lg">
                  <p><strong className="text-ink">Analytics:</strong> Google Analytics - tracks user behavior (pages visited, time on site)</p>
                  <p><strong className="text-ink">Marketing:</strong> Facebook Pixel - tracks conversions and enables targeted ads</p>
                </div>
              )}
            </div>
          </div>
          <div className="flex gap-3 flex-shrink-0 justify-end">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm font-medium text-mist hover:text-ink rounded-full border border-line hover:border-line-strong transition"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 text-sm font-semibold bg-brand hover:bg-brand-muted text-night rounded-full transition"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/** Primary CTA - renders a router Link when `to` is given, otherwise a button. `variant="outline"` for secondary actions */
const PrimaryButton = ({ to, onClick, children, variant = 'solid', size = 'lg', className = '' }) => {
    const variants = {
        solid: 'bg-brand hover:bg-brand-muted text-night shadow-lg shadow-brand/20 hover:shadow-brand/30',
        outline: 'border border-brand/60 text-brand hover:bg-brand/10 hover:border-brand',
    };
    const sizes = {
        lg: 'py-3.5 px-8 text-base md:text-lg',
        sm: 'py-2.5 px-5 text-sm',
    };
    const classes = `inline-flex items-center justify-center font-display font-semibold rounded-full transition duration-200 hover:-translate-y-0.5 ${variants[variant]} ${sizes[size]} ${className}`;

    return to
        ? <Link to={to} className={classes}>{children}</Link>
        : <button onClick={onClick} className={classes}>{children}</button>;
};

/** Small uppercase label above section headings, echoing the logo tagline */
const Eyebrow = ({ children, className = '' }) => (
    <p className={`font-display text-xs md:text-sm font-semibold uppercase tracking-[0.25em] text-peach ${className}`}>{children}</p>
);

/** Logo mark + wordmark, styled after the Skyewire logo */
const Logo = ({ size = 'md' }) => (
    <img
        src="/logo-full.png"
        alt="Skyewire Solutions"
        width="700"
        height="203"
        className={`w-auto ${size === 'md' ? 'h-10 md:h-12' : 'h-14'}`}
    />
);

/** Accessible modal dialog - closes on Escape or backdrop click */
const Modal = ({ title, onClose, children }) => {
    useEffect(() => {
        const handleKey = (e) => e.key === 'Escape' && onClose();
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                className="bg-panel border border-line rounded-2xl p-6 md:p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto text-left shadow-2xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-5">
                    <h2 className="font-display text-2xl font-bold text-ink">{title}</h2>
                    <button onClick={onClose} aria-label="Close" className="text-fog hover:text-ink p-1 rounded-full">
                        <X className="h-6 w-6" />
                    </button>
                </div>
                <div className="text-mist text-sm leading-relaxed space-y-3">{children}</div>
            </div>
        </div>
    );
};


// --- Components ---

/** Header component with navigation and logo */
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const getRoutePath = (page) => {
    if (page === 'home') return '/';
    if (page === 'services') return '/solutions';
    if (page === 'about') return '/about';
    if (page === 'contact') return '/contact';
    return '/';
  };

  const isActive = (page) => {
    const currentPath = location.pathname;
    return getRoutePath(page) === currentPath || (page === 'home' && currentPath === '/');
  };

  return (
    <header className="sticky top-0 z-50 bg-night/85 backdrop-blur-md border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="hover:opacity-90 transition" aria-label="Skyewire Solutions home">
            <Logo />
          </Link>

          <div className="hidden md:flex items-center gap-10">
            <nav className="flex gap-8">
              {navItems.filter(item => item.page !== 'contact').map((item) => (
                <Link
                  key={item.page}
                  to={getRoutePath(item.page)}
                  className={`text-sm font-medium transition duration-200 py-1 border-b-2 ${
                    isActive(item.page)
                      ? 'text-ink border-brand'
                      : 'text-mist border-transparent hover:text-ink hover:border-line-strong'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <PrimaryButton to="/contact" size="sm" className="hidden lg:inline-flex">
              Request Free Assessment
              <ArrowRight className="h-4 w-4 ml-2" />
            </PrimaryButton>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-mist hover:text-brand p-1"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div id="mobile-menu" className="md:hidden bg-abyss py-2 border-t border-line">
          {navItems.map((item) => (
            <Link
              key={item.page}
              to={getRoutePath(item.page)}
              onClick={() => setIsMenuOpen(false)}
              className={`block w-full text-left px-6 py-3 text-base font-medium transition duration-200 ${
                isActive(item.page)
                  ? 'text-brand bg-panel'
                  : 'text-mist hover:text-ink hover:bg-panel'
              }`}
            >
                <item.icon className="h-5 w-5 inline mr-3" />
                {item.name}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
};

/** Footer component */
const Footer = () => {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  return (
    <footer className="bg-abyss border-t border-line py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="flex flex-col items-center text-center text-fog">
          <Logo size="sm" />
          <p className="font-display text-[11px] font-medium uppercase tracking-[0.3em] text-fog mt-5">AI · Integration · Automation</p>
          <div className="flex justify-center gap-x-8 gap-y-2 mt-8 flex-wrap">
            <Link to="/contact" className="hover:text-brand transition text-sm">Contact Us</Link>
            <button onClick={() => setShowPrivacy(true)} className="hover:text-brand transition text-sm">Privacy Policy</button>
            <button onClick={() => setShowTerms(true)} className="hover:text-brand transition text-sm">Terms of Use</button>
            <Link to="/onboarding" className="hover:text-brand transition text-sm">Client Onboarding</Link>
          </div>
          <div className="w-full max-w-md h-px bg-line mt-8" />
          <p className="text-xs text-fog mt-6">&copy; {new Date().getFullYear()} Skyewire Solutions. All rights reserved.</p>
        </div>

        {/* Privacy Policy Modal */}
        {showPrivacy && (
          <Modal title="Privacy Policy" onClose={() => setShowPrivacy(false)}>
            <p><strong className="text-ink">Last Updated:</strong> January 2026</p>
            <p><strong className="text-ink">1. Information We Collect:</strong> Name, email, company name, and descriptions of your business operations and challenges. We do NOT ask for passwords, API keys, or sensitive data.</p>
            <p><strong className="text-ink">2. How We Use Your Information:</strong> To generate your personalized assessment report, send you follow-up communications, and improve our service quality.</p>
            <p><strong className="text-ink">3. AI Processing & Data Retention:</strong> Your data is processed using AI with zero data retention. Information is not stored or used for training by the AI provider.</p>
            <p><strong className="text-ink">4. Data Security:</strong> We use industry-standard encryption and secure servers. Access is limited to Spencer McCain and our assessment system.</p>
            <p><strong className="text-ink">5. Your Rights:</strong> You can request access, correction, or deletion of your information at any time.</p>
            <p><strong className="text-ink">Contact:</strong> spencer@skyewiresolutions.com</p>
          </Modal>
        )}

        {/* Terms of Service Modal */}
        {showTerms && (
          <Modal title="Terms of Use" onClose={() => setShowTerms(false)}>
            <p><strong className="text-ink">Last Updated:</strong> January 2026</p>
            <p><strong className="text-ink">1. Free Assessment:</strong> Provided as-is for informational purposes only. Not a binding proposal or guarantee of results.</p>
            <p><strong className="text-ink">2. Strategy Call:</strong> Requires booking confirmation. Rescheduling available with 48 hours notice. Advisory only; no commitment required.</p>
            <p><strong className="text-ink">3. Build Projects:</strong> Governed by separate project agreement defining deliverables, timeline, pricing, and payment schedule.</p>
            <p><strong className="text-ink">4. Retainers:</strong> Defined by separate retainer agreement with scope, hours, pricing, and cancellation terms (typically 30 days notice).</p>
            <p><strong className="text-ink">5. Limitation of Liability:</strong> Services provided as-is. We are not liable for indirect or consequential damages. Liability limited to amount paid.</p>
            <p><strong className="text-ink">6. Confidentiality:</strong> Both parties agree to keep business information confidential.</p>
            <p><strong className="text-ink">Contact:</strong> spencer@skyewiresolutions.com</p>
          </Modal>
        )}
      </div>
    </footer>
  );
};

/** Home Page View */
const Home = () => (
    <div>
        {/* Section 1: Hero - faint circuit grid with an aqua glow */}
        <section className="relative overflow-hidden bg-night min-h-[80vh] flex items-center">
            <div className="absolute inset-0 bg-grid-faint bg-grid [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" aria-hidden="true" />
            <div className="absolute inset-0 bg-hero-glow" aria-hidden="true" />
            <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-24">
                <Eyebrow className="mb-6">AI Automation + Security Consulting</Eyebrow>
                <h1 className="font-display text-4xl sm:text-5xl md:text-7xl font-bold text-ink mb-6 leading-[1.08] tracking-tight">
                    AI Automation &amp; Security, <span className="text-brand">Built to Scale Your Operations</span>
                </h1>
                <h2 className="text-lg md:text-xl text-mist mb-12 max-w-3xl mx-auto leading-relaxed">
                    Custom AI automation and secure infrastructure built specifically for electricians, plumbers, HVAC contractors, and other trade businesses. Work smarter, faster, and with confidence.
                </h2>
                <PrimaryButton to="/contact">
                    Request Free Assessment
                    <ArrowRight className="h-5 w-5 ml-3" />
                </PrimaryButton>
            </div>
        </section>

        {/* Section 2: The Pain Point */}
        <section className="bg-abyss border-y border-line py-20 md:py-28">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                    <Eyebrow className="mb-4">The Problem</Eyebrow>
                    <h2 className="font-display text-3xl md:text-5xl font-bold text-ink">
                        Running Your Business <span className="text-peach">the Hard Way?</span>
                    </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    <PainCard icon={Clock} title="Manual Data Transfer Hell" description="Your team spends hours each week manually copying client info, job details, and payment data between systems—CRM, accounting, job tracking, email." />
                    <PainCard icon={X} title="Systems That Don't Talk" description="You're using the right tools, but they're siloed. Information falls through the cracks. You can't see the full picture. Growth gets blocked by operational friction." />
                    <PainCard icon={Shield} title="Security + Compliance Gaps" description="As you scale, access control and data security become real problems. You need a system that's secure by design, not by luck." />
                </div>
            </div>
        </section>

        {/* Section 3: Value Proposition */}
        <section className="bg-night py-20 md:py-28">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-14">
                    <Eyebrow className="mb-4">The Solution</Eyebrow>
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-4">Enterprise-Grade Automation, Built Your Size</h2>
                    <p className="text-lg md:text-xl text-mist max-w-3xl mx-auto">
                        We've designed this stuff for enterprise. Now you get that expertise—built specifically for electricians, plumbers, HVAC companies, and general contractors—without the enterprise price tag.
                    </p>
                </div>
                <div className="grid md:grid-cols-3 gap-6">
                    <ValueCard icon={Zap} title="Intelligent Workflow Automation" description="Custom-built automations that eliminate repetitive data entry, connect your tools, and let AI handle the parts humans shouldn't." />
                    <ValueCard icon={Shield} title="Security That Actually Scales" description="Access control, data encryption, secure integrations—designed in from the start, not added later. Protect client data and your business." />
                    <ValueCard icon={TrendingUp} title="Proven at Every Scale" description="Enterprise experience brought to SMB reality. Your workflows run like they should—secure, fast, and stable." />
                </div>
            </div>
        </section>

        {/* Section 4: Solutions CTA */}
        <section className="relative overflow-hidden bg-abyss border-t border-line py-20 md:py-24 text-center px-4">
            <div className="absolute inset-0 bg-hero-glow" aria-hidden="true" />
            <div className="relative">
                <h2 className="font-display text-3xl md:text-4xl font-bold text-ink mb-4">Four Ways We Help You Scale</h2>
                <p className="text-lg md:text-xl text-mist mb-10 max-w-3xl mx-auto">
                    From free assessment to ongoing partnership, we're here to eliminate manual work and keep your automation secure and running.
                </p>
                <PrimaryButton to="/solutions" variant="outline">
                    View Our Solutions
                    <ArrowRight className="h-5 w-5 ml-2" />
                </PrimaryButton>
                <p className="text-fog text-sm mt-8">
                    Or <Link to="/about" className="text-brand underline underline-offset-4 hover:text-brand-muted">learn about our philosophy</Link>
                </p>
            </div>
        </section>
    </div>
);

/** Reusable Pain Card component for Home page */
const PainCard = ({ icon: Icon, title, description }) => (
    <div className="bg-panel rounded-2xl p-7 border border-line border-t-2 border-t-peach/70 flex flex-col h-full transition duration-300 hover:-translate-y-1 hover:border-peach/40">
        <div className="h-11 w-11 rounded-xl bg-peach/15 border border-peach/25 flex items-center justify-center mb-5">
            <Icon className="h-5 w-5 text-peach" aria-label={title} />
        </div>
        <h4 className="font-display text-lg font-semibold text-ink mb-2">{title}</h4>
        <p className="text-mist leading-relaxed">{description}</p>
    </div>
);

/** Reusable Value Card component for Home page */
const ValueCard = ({ icon: Icon, title, description }) => (
    <div className="group bg-panel rounded-2xl p-8 border border-line hover:border-brand-muted hover:-translate-y-1 hover:shadow-xl hover:shadow-brand/10 transition duration-300 flex flex-col items-start h-full">
        <div className="h-12 w-12 rounded-xl bg-brand/10 border border-brand/20 flex items-center justify-center mb-6 group-hover:bg-brand/20 transition">
            <Icon className="h-6 w-6 text-brand" aria-label={title} />
        </div>
        <h4 className="font-display text-xl font-semibold text-ink mb-3">{title}</h4>
        <p className="text-mist text-base md:text-lg leading-relaxed">{description}</p>
    </div>
);


/** Services Page View - Four Tiers */
const Services = () => (
    <div className="relative bg-night">
        <div className="absolute inset-x-0 top-0 h-[480px] bg-top-glow" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
            <div className="text-center mb-16 md:mb-20">
                <Eyebrow className="mb-4">Solutions</Eyebrow>
                <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold text-ink mb-5 leading-tight">Our Solutions: From Free Assessment to Ongoing Partnership</h1>
                <p className="text-lg md:text-xl text-mist max-w-3xl mx-auto">
                    Every successful automation starts with clarity. Here's how we work—from understanding your current state to keeping everything running secure and fast. Built for electricians, plumbers, HVAC companies, and general contractors ready to scale past manual operations.
                </p>
            </div>

            {/* --- SERVICE TIERS GRID --- */}
            <div className="space-y-8 mb-20">
                {servicePillars.map((pillar, index) => (
                    <PillarSection key={index} pillar={pillar} index={index} />
                ))}
            </div>

            {/* --- FAQ --- mirrored into FAQPage schema via faqItems, see seoHelpers.js */}
            <div className="mb-20">
                <div className="text-center mb-12">
                    <Eyebrow className="mb-4">Common Questions</Eyebrow>
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-ink">Before You Reach Out</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5 max-w-4xl mx-auto">
                    {faqItems.map((faq, index) => (
                        <div key={index} className="bg-panel rounded-2xl p-6 border border-line">
                            <h3 className="font-display text-base font-semibold text-ink mb-2">{faq.question}</h3>
                            <p className="text-mist text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Final CTA to move to Contact page */}
            <div className="relative overflow-hidden text-center p-8 md:p-12 bg-panel rounded-2xl border border-line">
                <div className="absolute inset-0 bg-hero-glow" aria-hidden="true" />
                <div className="relative">
                    <h3 className="font-display text-3xl md:text-4xl font-bold text-ink mb-4">Ready to Get Started?</h3>
                    <p className="text-lg md:text-xl text-mist mb-8 max-w-3xl mx-auto">
                        Begin with a free assessment. No commitment, no upfront cost. Just a personalized report with clear next steps.
                    </p>
                    <PrimaryButton to="/contact">
                        Request Free Assessment
                        <ArrowRight className="h-5 w-5 ml-3" />
                    </PrimaryButton>
                    <p className="text-fog text-sm mt-6">
                        Questions? <Link to="/about" className="text-brand underline underline-offset-4 hover:text-brand-muted">Learn more about us</Link>
                    </p>
                </div>
            </div>
        </div>
    </div>
);

/** Pillar Section Component - one card per service tier */
const PillarSection = ({ pillar }) => (
    <div className="bg-panel rounded-2xl p-6 md:p-10 border border-line">
        <div className="flex items-start sm:items-center gap-5 mb-8">
            <div className="h-14 w-14 rounded-2xl bg-brand/10 border border-brand/25 flex items-center justify-center flex-shrink-0">
                <pillar.icon className="h-7 w-7 text-brand" />
            </div>
            <div>
                <p className="font-display text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-peach mb-1">{pillar.name}</p>
                <h2 className="font-display text-xl md:text-2xl font-bold text-ink">{pillar.description}</h2>
            </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
            {pillar.services.map((service, i) => (
                <ServiceDetailCard key={i} service={service} />
            ))}
        </div>
    </div>
);

/** Service Detail Card for Pillars */
const ServiceDetailCard = ({ service }) => (
    <div className="bg-raised/60 rounded-xl p-5 md:p-6 border border-line h-full flex flex-col hover:border-brand-muted transition duration-300">
        <h4 className="font-display text-base font-semibold text-ink mb-2 flex items-center">
            <ArrowRight className="h-4 w-4 text-brand mr-2 flex-shrink-0" />
            {service.name}
        </h4>
        <p className="text-mist text-sm leading-relaxed flex-grow">{service.detail}</p>
    </div>
);


/** About Us Page View */
const About = () => (
    <div className="relative bg-night">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
            <div className="text-center mb-14">
                <Eyebrow className="mb-4">About Us</Eyebrow>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-ink mb-4">The Skyewire Philosophy</h1>
                <p className="text-lg md:text-xl text-mist">
                    Enterprise-grade automation and security, built for growing service and trade businesses.
                </p>
            </div>

            <div className="bg-panel p-6 md:p-12 rounded-2xl space-y-12 border border-line">
                {/* Mission Section */}
                <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-ink border-b border-line pb-3 mb-6">Our Mission</h2>
                    <p className="text-lg text-mist leading-relaxed mb-4">
                        Skyewire Solutions was founded to bring enterprise-level automation and security to small and medium-sized service businesses—electricians, plumbers, HVAC companies, and general contractors—the kind that are tired of manual work but don't need (or can't afford) enterprise consulting fees.
                    </p>
                    <p className="text-lg text-mist leading-relaxed">
                        Most automation consultants can wire up workflows. Few can secure them. We do both. We've spent over 2 years designing and implementing automation at million-dollar and billion-dollar scales. Now we bring that expertise down to the businesses that need it most: service companies, contractors, and trade businesses scaling beyond manual operations.
                    </p>
                </div>

                {/* Founder Section */}
                <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-ink border-b border-line pb-3 mb-6">Meet Spencer (Founder & Principal Consultant)</h2>
                    <div className="flex flex-col md:flex-row items-center md:items-start gap-6 md:gap-8">
                        <div className="w-28 h-28 bg-brand rounded-full flex items-center justify-center font-display text-2xl font-bold text-night flex-shrink-0 ring-2 ring-peach/60 ring-offset-4 ring-offset-panel">
                            SM
                        </div>
                        <div className='flex-grow border-l-2 border-peach/60 pl-6'>
                            <p className="text-mist italic text-lg leading-relaxed mb-4">
                                "I've watched teams waste 10+ hours a week on manual data entry and spreadsheet hell. I've also seen what enterprise-grade automation looks like when done right—secure, fast, and so reliable it becomes invisible. Skyewire exists because every business deserves access to that level of work, without the enterprise price tag or bureaucracy."
                            </p>
                            <p className="font-display font-semibold text-brand">— Spencer McCain, Automation & Security Consultant</p>
                            <p className="text-sm text-fog mt-2">2+ years building automation at scale | Service & trade business specialist | Direct expert partnership</p>
                        </div>
                    </div>
                </div>

                {/* Solo Consultant Advantage */}
                <div>
                    <h2 className="font-display text-2xl md:text-3xl font-bold text-ink border-b border-line pb-3 mb-6">The Independent Consultant Advantage</h2>
                    <ul className="grid md:grid-cols-3 gap-4 text-mist">
                        <li className="p-5 bg-raised/60 rounded-xl border border-line"><strong className="block font-display text-brand mb-1">Direct Access</strong>You work directly with the technical expert building your solution—not a junior, not an account manager.</li>
                        <li className="p-5 bg-raised/60 rounded-xl border border-line"><strong className="block font-display text-brand mb-1">Low Overhead</strong>High-value expertise without the expensive agency price tag or markup.</li>
                        <li className="p-5 bg-raised/60 rounded-xl border border-line"><strong className="block font-display text-brand mb-1">Focused Dedication</strong>Your project receives undivided attention and prioritized execution.</li>
                    </ul>
                </div>

                {/* CTA Links */}
                <div className="text-center pt-8 border-t border-line">
                    <p className="text-mist mb-6">Ready to explore how we can help?</p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <PrimaryButton to="/contact" size="sm">Start Your Free Assessment</PrimaryButton>
                        <PrimaryButton to="/solutions" size="sm" variant="outline">View Our Solutions</PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
);


/** Onboarding Page - for new clients; linked only from the footer */
const Onboarding = () => (
    <div className="relative bg-night">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
            <div className="text-center mb-14">
                <Eyebrow className="mb-4">Client Onboarding</Eyebrow>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-ink">Welcome to Skyewire</h1>
            </div>

            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {onboardingTiles.map((tile) => {
                    // Tiles with a destination render as links; the rest stay static until their pages exist
                    const Tile = tile.to ? Link : 'div';
                    return (
                        <Tile key={tile.title} to={tile.to} className="bg-panel rounded-2xl p-8 border border-line hover:border-brand-muted hover:-translate-y-1 transition duration-300 flex flex-col items-center text-center">
                            <div className="h-14 w-14 rounded-2xl bg-brand/10 border border-brand/25 flex items-center justify-center mb-5">
                                <tile.icon className="h-7 w-7 text-brand" />
                            </div>
                            <h2 className="font-display text-lg font-semibold text-ink">{tile.title}</h2>
                        </Tile>
                    );
                })}
            </div>
        </div>
    </div>
);

// Shared input styling for all forms
const inputClasses = "mt-2 block w-full rounded-xl border border-line bg-night text-ink placeholder:text-fog/70 p-3.5 transition focus:border-brand focus:ring-2 focus:ring-brand/30 focus:outline-none [color-scheme:dark]";

/** Labeled form field wrapper; marks required fields with an asterisk */
const FormField = ({ id, label, required, hint, children }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-ink">
            {label}{required && <span className="text-peach ml-1">*</span>}
        </label>
        {hint && <p className="text-xs text-fog mt-1">{hint}</p>}
        {children}
    </div>
);

/** Titled group of fields inside the requirements form */
const FormSection = ({ title, children }) => (
    <section className="bg-panel p-6 md:p-8 rounded-2xl border border-line space-y-6">
        <h2 className="font-display text-xl font-bold text-ink border-b border-line pb-3">{title}</h2>
        {children}
    </section>
);

// Select options for the requirements form
const projectTypes = ['Workflow automation', 'Integration between tools', 'AI assistant or agent', 'Custom tool or utility', 'Security review', 'Other'];
const teamSizes = ['1–5', '6–20', '21–50', '50+'];

/** Submit Project Requirements Page - opened from the Onboarding tiles */
const SubmitRequirements = () => {
    const [submitted, setSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setErrorMessage('');

        const formData = Object.fromEntries(new FormData(e.target));

        try {
            // Creates a Zoho task linked to the company's account and contact
            const response = await fetch('/api/save-requirements.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.error('Server returned a non-JSON response:', response.status, parseError);
                setErrorMessage(`The server returned an unexpected response (${response.status}). Please try again, or email spencer@skyewiresolutions.com.`);
                setIsLoading(false);
                return;
            }

            if (!response.ok) {
                setErrorMessage(result.error || 'Failed to submit your requirements. Please try again.');
                setIsLoading(false);
                return;
            }

            setIsLoading(false);
            setSubmitted(true);
            window.scrollTo(0, 0);

        } catch (error) {
            console.error('Requirements submission error:', error);
            setErrorMessage('Network error. Please check your connection and try again.');
            setIsLoading(false);
        }
    };

    return (
        <div className="relative bg-night">
            <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
            <div className="relative max-w-3xl mx-auto px-4 sm:px-6 pt-16 pb-24">
                <Link to="/onboarding" className="text-sm text-fog hover:text-brand transition">&larr; Back to Onboarding</Link>
                <div className="text-center mt-8 mb-10">
                    <Eyebrow className="mb-4">Client Onboarding</Eyebrow>
                    <h1 className="font-display text-3xl md:text-5xl font-bold text-ink mb-4">Submit Project Requirements</h1>
                    <p className="text-lg text-mist">Tell us what you need built. The more detail you share, the faster we can scope your project.</p>
                </div>

                {submitted ? (
                    <div className="bg-panel p-8 md:p-12 rounded-2xl border border-line text-center">
                        <CheckCircle2 className="h-14 w-14 text-brand mx-auto mb-5" />
                        <h2 className="font-display text-2xl md:text-3xl font-bold text-ink mb-3">Requirements Received</h2>
                        <p className="text-mist mb-8">Thanks! We'll review your requirements and follow up with next steps.</p>
                        <PrimaryButton to="/onboarding" size="sm">Back to Onboarding</PrimaryButton>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <FormSection title="Contact Information">
                            <div className="grid sm:grid-cols-2 gap-6">
                                <FormField id="firstName" label="First Name" required>
                                    <input id="firstName" name="firstName" type="text" required className={inputClasses} placeholder="Jane" />
                                </FormField>
                                <FormField id="lastName" label="Last Name" required>
                                    <input id="lastName" name="lastName" type="text" required className={inputClasses} placeholder="Doe" />
                                </FormField>
                                <FormField id="company" label="Company" required hint="Must match your company name in our records.">
                                    <input id="company" name="company" type="text" required className={inputClasses} placeholder="Doe Plumbing LLC" />
                                </FormField>
                                <FormField id="email" label="Email" required>
                                    <input id="email" name="email" type="email" required className={inputClasses} placeholder="jane@yourcompany.com" />
                                </FormField>
                                <FormField id="phone" label="Phone">
                                    <input id="phone" name="phone" type="tel" className={inputClasses} placeholder="(555) 123-4567" />
                                </FormField>
                            </div>
                        </FormSection>

                        <FormSection title="Project Details">
                            <FormField id="projectType" label="Project Type" required>
                                <select id="projectType" name="projectType" required defaultValue="" className={inputClasses}>
                                    <option value="" disabled>Select one</option>
                                    {projectTypes.map((type) => <option key={type}>{type}</option>)}
                                </select>
                            </FormField>
                            <FormField id="problem" label="What problem are we solving?" required hint="Describe the current process and what's not working.">
                                <textarea id="problem" name="problem" rows="4" required className={inputClasses} placeholder="e.g., We copy every new lead from email into our CRM by hand, which takes hours each week." />
                            </FormField>
                            <FormField id="outcome" label="What does success look like?" required>
                                <textarea id="outcome" name="outcome" rows="3" required className={inputClasses} placeholder="e.g., New leads appear in the CRM automatically within minutes." />
                            </FormField>
                        </FormSection>

                        <FormSection title="Tools & Scope">
                            <FormField id="tools" label="Tools & Software Involved" required hint="List every system this project touches.">
                                <textarea id="tools" name="tools" rows="3" required className={inputClasses} placeholder="e.g., Gmail, HubSpot, QuickBooks, Jobber" />
                            </FormField>
                            <div className="grid sm:grid-cols-2 gap-6">
                                <FormField id="teamSize" label="Team Members Affected">
                                    <select id="teamSize" name="teamSize" defaultValue="" className={inputClasses}>
                                        <option value="">Select one</option>
                                        {teamSizes.map((size) => <option key={size}>{size}</option>)}
                                    </select>
                                </FormField>
                                <FormField id="volume" label="Approximate Volume" hint="Records, jobs, or requests per week.">
                                    <input id="volume" name="volume" type="text" className={inputClasses} placeholder="e.g., 50 leads per week" />
                                </FormField>
                            </div>
                            <FormField id="compliance" label="Security or Compliance Requirements">
                                <textarea id="compliance" name="compliance" rows="2" className={inputClasses} placeholder="e.g., Client data must stay in the US" />
                            </FormField>
                        </FormSection>

                        <FormSection title="Timeline">
                            <FormField id="targetDate" label="Target Launch Date">
                                <input id="targetDate" name="targetDate" type="date" className={inputClasses} />
                            </FormField>
                        </FormSection>

                        <FormSection title="Anything Else?">
                            <FormField id="notes" label="Additional Notes">
                                <textarea id="notes" name="notes" rows="4" className={inputClasses} placeholder="Deadlines, examples, links, or anything else we should know." />
                            </FormField>
                        </FormSection>

                        {errorMessage && (
                            <div className="p-5 bg-red-950/40 border border-red-400/30 rounded-xl text-center">
                                <p className="text-red-200">{errorMessage}</p>
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading}
                            className="w-full flex justify-center items-center py-3.5 px-4 rounded-full font-display font-semibold text-base text-night bg-brand hover:bg-brand-muted transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? (
                                <>
                                    <Loader className="h-5 w-5 animate-spin mr-3" />
                                    Submitting...
                                </>
                            ) : (
                                <>
                                    Submit Requirements
                                    <ArrowRight className="h-5 w-5 ml-3" />
                                </>
                            )}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
};

/** Numbered section of a legal document */
const DocSection = ({ title, children }) => (
    <section className="space-y-4">
        <h2 className="font-display text-xl md:text-2xl font-bold text-ink border-b border-line pb-3">{title}</h2>
        {children}
    </section>
);

/** Bulleted list for legal documents */
const DocList = ({ items }) => (
    <ul className="list-disc pl-6 space-y-2 marker:text-brand">
        {items.map((item) => <li key={item}>{item}</li>)}
    </ul>
);

/** Privacy Policy Page - /documents/Privacy-Policy */
const PrivacyPolicy = () => (
    <div className="relative bg-night">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24">
            <div className="text-center mb-12">
                <Eyebrow className="mb-4">Skyewire Solutions</Eyebrow>
                <h1 className="font-display text-4xl md:text-5xl font-bold text-ink mb-5">Privacy Policy</h1>
                <p className="text-sm text-fog">Effective Date: ______________ | Last Updated: ______________</p>
            </div>

            <div className="bg-panel p-6 md:p-12 rounded-2xl border border-line space-y-10 text-mist leading-relaxed">
                <p className="text-sm text-fog italic border-l-2 border-peach/60 pl-4">
                    This is a draft template for general informational purposes only and is not legal advice. Have it reviewed by a licensed attorney before publishing it on your website, and confirm it satisfies any laws that apply to your visitors (e.g., GDPR, CCPA/CPRA) based on where they're located.
                </p>

                <DocSection title="1. Introduction & Scope">
                    <p>Skyewire Solutions ("Skyewire," "we," "us," or "our") operates skyewiresolutions.com (the "Site") and related tools, including our free AI-powered automation/cybersecurity assessment tool (the "Assessment Tool"). This Privacy Policy explains what information we collect, how we use and share it, and the choices you have, when you visit the Site, use the Assessment Tool, or otherwise interact with us.</p>
                    <p>By using the Site, you agree to the collection and use of information as described in this Privacy Policy. If you do not agree, please do not use the Site.</p>
                </DocSection>

                <DocSection title="2. Information We Collect">
                    <h3 className="font-display font-semibold text-ink">Information you provide to us:</h3>
                    <DocList items={[
                        'Contact information (name, email, phone number, company name) when you fill out a form, request a strategy call, or contact us',
                        'Assessment Tool inputs: information about your software stack (e.g., Microsoft 365, Salesforce, identity providers), manual processes, and automation goals that you voluntarily submit to receive a personalized report',
                        'Payment and billing information (processed by Stripe; see Section 4 — we do not store full card numbers on our own servers)',
                        'Any other information you choose to share, e.g., in emails, call notes, or comments',
                    ]} />
                    <h3 className="font-display font-semibold text-ink">Information collected automatically:</h3>
                    <DocList items={[
                        'Usage data (pages visited, time on page, referring URLs, browser/device type, approximate location via IP address)',
                        'Cookies and similar tracking technologies (see Section 6)',
                    ]} />
                    <h3 className="font-display font-semibold text-ink">Information from third parties:</h3>
                    <DocList items={[
                        "Information from social platforms (e.g., our Facebook page) when you interact with our content, subject to that platform's own privacy policy",
                    ]} />
                </DocSection>

                <DocSection title="3. How We Use Information">
                    <p>We use the information we collect to:</p>
                    <DocList items={[
                        'Generate and deliver your personalized Assessment Tool report',
                        'Respond to inquiries and schedule strategy/discovery calls',
                        'Provide, maintain, and improve the Site and our Services',
                        'Process payments and manage invoicing via Stripe',
                        'Send you marketing communications, which you may opt out of at any time (see Section 7)',
                        'Understand how visitors use the Site to improve content and design',
                        'Comply with legal obligations and enforce our Terms of Service',
                    ]} />
                    <p>We do not use the information you submit through the Assessment Tool to make automated decisions that produce legal or similarly significant effects about you without human involvement.</p>
                </DocSection>

                <DocSection title="4. Data Sharing & Third Parties">
                    <p>We do not sell your personal information. We share information only as follows:</p>
                    <DocList items={[
                        "Payment processing: Stripe processes payments on our behalf and receives the billing information necessary to do so, subject to Stripe's Privacy Policy.",
                        'AI/processing tools: Where the Assessment Tool uses third-party AI models or APIs to generate your report, we apply zero- or minimal-retention settings where available and share only the information reasonably necessary to generate your report.',
                        'Service providers: Vendors who help us operate the Site, host data, send email, or provide analytics, under confidentiality obligations and only to perform services for us.',
                        'Legal requirements: When required by law, subpoena, or to protect the rights, property, or safety of Skyewire, our clients, or others.',
                        'Business transfers: In connection with a merger, acquisition, or sale of assets, subject to standard protections for the transferred information.',
                    ]} />
                    <p>______________ [List any specific named third-party tools/platforms used, e.g., n8n, email marketing platform, analytics provider]</p>
                </DocSection>

                <DocSection title="5. Data Retention & Security">
                    <p><strong className="text-ink">Retention.</strong> We retain personal information, including Assessment Tool submissions, only as long as reasonably necessary to fulfill the purposes described in this Policy, or as required by law. Assessment Tool inputs describing your infrastructure are retained for ______________ [e.g., 12 months] and then deleted or anonymized unless you engage us for further Services.</p>
                    <p><strong className="text-ink">Security.</strong> We use commercially reasonable administrative, technical, and physical safeguards designed to protect your information, including minimizing the data we collect through the Assessment Tool and applying zero/minimal-retention settings on third-party AI processing tools where available. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.</p>
                </DocSection>

                <DocSection title="6. Cookies & Tracking">
                    <p>We use cookies and similar technologies to operate the Site, remember preferences, and understand how visitors use the Site (e.g., analytics). Categories of cookies we use:</p>
                    <DocList items={[
                        'Essential cookies: required for the Site to function',
                        'Analytics cookies: ______________ [e.g., Google Analytics] — help us understand visitor behavior',
                        'Marketing cookies: ______________ [e.g., Facebook Pixel] — support advertising and social content performance',
                    ]} />
                    <p>You can control cookies through your browser settings. Disabling cookies may affect Site functionality. ______________ [Add a cookie consent banner/tool reference if used, and note any Do Not Track handling]</p>
                </DocSection>

                <DocSection title="7. Your Rights & Choices">
                    <p>Depending on your location, you may have the right to:</p>
                    <DocList items={[
                        'Access, correct, or delete the personal information we hold about you',
                        'Object to or restrict certain processing',
                        'Request a copy of your information in a portable format',
                        'Opt out of marketing emails at any time via the unsubscribe link, or by contacting us',
                        'Opt out of the sale or sharing of personal information (we do not sell personal information)',
                    ]} />
                    <p>To exercise any of these rights, contact us using the information in Section 10. We will respond within the time required by applicable law.</p>
                    <p>______________ [If you target EU/UK or California residents, add specific GDPR (Art. 15–21) or CCPA/CPRA rights language and any required "Do Not Sell or Share" link]</p>
                </DocSection>

                <DocSection title="8. Children's Privacy">
                    <p>The Site and Services are intended for business use and are not directed to individuals under 18. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us and we will delete it.</p>
                </DocSection>

                <DocSection title="9. Changes to This Policy">
                    <p>We may update this Privacy Policy from time to time. We will post the revised version on this page and update the "Last Updated" date above. Material changes will be highlighted via ______________ [e.g., a Site banner or email notice]. Your continued use of the Site after changes take effect constitutes acceptance of the revised Policy.</p>
                </DocSection>

                <DocSection title="10. Contact Us">
                    <p>If you have questions about this Privacy Policy or wish to exercise your rights, contact us at:</p>
                    <address className="not-italic">
                        <p className="font-semibold text-ink">Skyewire Solutions</p>
                        <p>Email: <a href="mailto:spencer@skyewiresolutions.com" className="text-brand underline underline-offset-4 hover:text-brand-muted">spencer@skyewiresolutions.com</a></p>
                        <p>Phone: <a href="tel:+18017033986" className="text-brand underline underline-offset-4 hover:text-brand-muted">(801)703-3986</a></p>
                    </address>
                </DocSection>
            </div>
        </div>
    </div>
);

/** Thank You Page - New Dedicated Route */
const ThankYou = () => (
    <div className="relative bg-night">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
        <div className="relative pt-20 pb-24 max-w-3xl mx-auto text-center px-4">
            <div className="bg-panel p-8 md:p-12 rounded-2xl border border-line">
                <CheckCircle2 className="h-16 w-16 text-brand mx-auto mb-6" />
                <h1 className="font-display text-3xl md:text-5xl font-bold text-ink mb-4">Assessment Submitted Successfully!</h1>
                <p className="text-xl text-mist mb-6">
                    Your AI Automation & Security Assessment is being generated.
                </p>
                <p className="text-lg text-mist mb-10">
                    You should receive your personalized report in your inbox within 1 hour. It will include key automation opportunities, potential impact, security observations, and your next step: booking a strategy call to dig deeper.
                </p>
                <div className="space-y-5">
                    <p className="text-sm text-fog">
                        In the meantime, check out our Solutions page to see the full range of how we help businesses like yours.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <PrimaryButton to="/" size="sm">Back to Home</PrimaryButton>
                        <PrimaryButton to="/solutions" size="sm" variant="outline">View Our Solutions</PrimaryButton>
                    </div>
                </div>
            </div>
        </div>
    </div>
);

/** Contact Page View */
const Contact = () => {
    const navigate = useNavigate();
    const [status, setStatus] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatus(null);
        setErrorMessage('');

        // Shared with the server so its Meta Conversions API / GA4 Measurement
        // Protocol calls (api/tracking.php) can dedupe against this browser event
        // instead of double-counting the same conversion.
        const eventId = generateEventId();
        const gaClientId = getGa4ClientId();

        // Get form data
        const formData = {
            company: e.target.company.value,
            firstName: e.target.firstName.value,
            lastName: e.target.lastName.value,
            phone: e.target.phone.value,
            email: e.target.email.value,
            description: e.target.description.value,
            attribution: AttributionManager.get(),
            eventId,
            gaClientId,
        };

        try {
            // Send to PHP backend
            const response = await fetch('/api/save-contact.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });

            // A PHP error (or a missing endpoint) returns HTML, so report that plainly
            // instead of letting JSON.parse fail and look like a connection problem
            let result;
            try {
                result = await response.json();
            } catch (parseError) {
                console.error('Server returned a non-JSON response:', response.status, parseError);
                setStatus('error');
                setErrorMessage(`The server returned an unexpected response (${response.status}). Please try again, or email spencer@skyewiresolutions.com.`);
                setIsLoading(false);
                return;
            }

            if (!response.ok) {
                setStatus('error');
                setErrorMessage(result.error || 'Failed to submit form. Please try again.');
                setIsLoading(false);
                return;
            }

            // Track conversion. eventId lets the server-side backstop (fired from
            // save-contact.php) dedupe against this client-side event.
            trackFacebookEvent('Lead', { currency: 'USD' }, eventId);
            trackGoogleEvent('form_submit', {
              form_name: 'contact_form',
              email: formData.email
            });

            // Navigate to thank you page
            setIsLoading(false);
            navigate('/thank-you');

        } catch (error) {
            console.error('Form submission error:', error);
            setStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
            setIsLoading(false);
        }
    };


    return (
        <div className="relative bg-night">
            <div className="absolute inset-x-0 top-0 h-[420px] bg-top-glow" aria-hidden="true" />
            <div className="relative pt-16 pb-24 max-w-3xl mx-auto px-4 sm:px-6">
                <div className="text-center mb-10">
                    <Eyebrow className="mb-4">Free Assessment</Eyebrow>
                    <h1 className="font-display text-3xl md:text-4xl font-bold text-ink mb-5 leading-tight">Get Your Free AI Automation & Security Assessment</h1>
                    <p className="text-lg text-mist leading-relaxed">
                        Answer a few quick questions about your operations, systems, and biggest pain points. We'll analyze your situation and send you a personalized report with automation opportunities and clear next steps. Takes about 10 minutes. It's free. No sales call required unless you want one.
                    </p>
                </div>

                <div className="bg-panel p-6 md:p-8 rounded-2xl border border-line mb-10">
                    {status === 'error' ? (
                        <div className="text-center p-8 bg-red-950/40 border border-red-400/30 rounded-xl">
                            <X className="h-10 w-10 text-red-300 mx-auto mb-4" />
                            <h2 className="font-display text-2xl font-bold text-ink mb-2">Submission Error</h2>
                            <p className="text-red-200 mb-6">{errorMessage}</p>
                            <PrimaryButton onClick={() => setStatus(null)} size="sm">
                                Try Again
                            </PrimaryButton>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Company - becomes the Account record in Zoho CRM */}
                            <FormField id="company" label="Company Name" required>
                                <input type="text" id="company" name="company" required className={inputClasses} placeholder="Doe Plumbing LLC" />
                            </FormField>

                            {/* Contact details */}
                            <div className="grid sm:grid-cols-2 gap-6">
                                <FormField id="firstName" label="First Name" required>
                                    <input type="text" id="firstName" name="firstName" required className={inputClasses} placeholder="Jane" />
                                </FormField>
                                <FormField id="lastName" label="Last Name" required>
                                    <input type="text" id="lastName" name="lastName" required className={inputClasses} placeholder="Doe" />
                                </FormField>
                                <FormField id="phone" label="Phone" required>
                                    <input type="tel" id="phone" name="phone" required className={inputClasses} placeholder="(555) 123-4567" />
                                </FormField>
                                <FormField id="email" label="Business Email" required>
                                    <input type="email" id="email" name="email" required className={inputClasses} placeholder="jane@yourcompany.com" />
                                </FormField>
                            </div>

                            {/* Crucial Qualification Field */}
                            <FormField id="description" label="Description of Work" required>
                                <textarea
                                    id="description"
                                    name="description"
                                    rows="4"
                                    required
                                    className={inputClasses}
                                    placeholder="e.g., Manually entering 50 leads a day from email into our CRM"
                                ></textarea>
                            </FormField>

                            {/* Submit Button */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="w-full flex justify-center items-center py-3.5 px-4 rounded-full font-display font-semibold text-base text-night bg-brand hover:bg-brand-muted transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isLoading ? (
                                    <>
                                        <Loader className="h-5 w-5 animate-spin mr-3" />
                                        Generating Your Assessment...
                                    </>
                                ) : (
                                    'Generate My Free Assessment Report'
                                )}
                            </button>
                        </form>
                    )}
                </div>

                <div className="text-center">
                    <p className="text-mist mb-3">Want to explore our solutions first?</p>
                    <Link to="/solutions" className="text-brand underline underline-offset-4 hover:text-brand-muted">View Our Solutions</Link>
                </div>
            </div>
        </div>
    );
};

// --- Page Tracker & SEO Component ---
const PageTracker = () => {
    const location = useLocation();

    useEffect(() => {
        const baseUrl = 'https://skyewiresolutions.com';

        // Page metadata for tracking and SEO. `robots` keeps client-only utility
        // pages and the post-submit thank-you page out of the index without
        // blocking crawling (blocking via robots.txt would hide this tag instead).
        const pageMetadata = {
            '/': {
                name: 'Home',
                title: 'Skyewire Solutions - AI Automation & Security for Electricians, Plumbers & HVAC',
                description: 'Custom AI automation and security consulting for electricians, plumbers, HVAC contractors, and other trade businesses. Work smarter, faster, with confidence.',
                pageType: 'home',
                robots: 'index, follow',
            },
            '/solutions': {
                name: 'Solutions',
                title: 'Automation Solutions for Trade Businesses - Skyewire Solutions',
                description: 'From a free assessment to a done-for-you build and ongoing support - a four-tier automation and security model built for electricians, plumbers, HVAC, and contractors.',
                pageType: 'solutions',
                robots: 'index, follow',
            },
            '/about': {
                name: 'About Us',
                title: 'About Spencer McCain - Skyewire Solutions',
                description: 'Meet Spencer McCain, founder of Skyewire Solutions. Enterprise automation expertise brought to service and trade businesses.',
                pageType: 'about',
                robots: 'index, follow',
            },
            '/contact': {
                name: 'Contact',
                title: 'Get Your Free Automation & Security Assessment - Skyewire Solutions',
                description: 'Request a free AI automation and security assessment for your electrical, plumbing, HVAC, or contracting business.',
                pageType: 'contact',
                robots: 'index, follow',
            },
            '/onboarding': {
                name: 'Onboarding',
                title: 'Client Onboarding - Skyewire Solutions',
                description: 'What to expect and what to prepare when starting a project with Skyewire Solutions.',
                pageType: 'onboarding',
                robots: 'noindex, follow',
            },
            '/onboarding/submit_requirements': {
                name: 'Submit Project Requirements',
                title: 'Submit Project Requirements - Skyewire Solutions',
                description: 'Submit your project requirements to Skyewire Solutions.',
                pageType: 'submitRequirements',
                robots: 'noindex, follow',
            },
            '/documents/Privacy-Policy': {
                name: 'Privacy Policy',
                title: 'Privacy Policy - Skyewire Solutions',
                description: 'How Skyewire Solutions collects, uses, and protects your information.',
                pageType: 'privacyPolicy',
                robots: 'index, follow',
            },
            '/thank-you': {
                name: 'Thank You',
                title: 'Assessment Submitted - Skyewire Solutions',
                description: 'Your assessment has been submitted. Check your email for your personalized report.',
                pageType: 'thankYou',
                robots: 'noindex, follow',
            }
        };

        const metadata = pageMetadata[location.pathname] || pageMetadata['/'];

        // Update meta tags
        const canonicalUrl = baseUrl + (location.pathname === '/' ? '' : location.pathname);
        updateMetaTags(metadata.title, metadata.description, '', canonicalUrl);
        setRobotsMeta(metadata.robots);

        // Inject page-specific schema. Solutions also carries FAQPage schema
        // alongside its CollectionPage schema.
        const pageSchema = metadata.pageType === 'solutions'
            ? [generatePageSchema('solutions', { pillars: servicePillars }), generateFAQSchema(faqItems)]
            : generatePageSchema(metadata.pageType);
        injectSchema(pageSchema);

        // Track page view
        trackPageView(metadata.name);

        // Scroll to top
        window.scrollTo(0, 0);
    }, [location.pathname]);

    return null;
};

// --- Main Application Component ---
const AppContent = () => {
    const [cookieConsent, setCookieConsent] = useState(false);

    // Initialize tracking on mount
    useEffect(() => {
        AttributionManager.capture();

        const prefs = CookieManager.getCookiePreferences();
        if (prefs) {
            setCookieConsent(true);
            if (prefs.analytics) {
                initializeGoogleAnalytics();
                initializePageSense();
            }
            if (prefs.marketing) initializeFacebookPixel();
        }
    }, []);

    return (
        <div className="min-h-screen bg-night font-sans antialiased text-ink">
            <PageTracker />
            <Header />
            <main>
                <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/solutions" element={<Services />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/onboarding" element={<Onboarding />} />
                    <Route path="/onboarding/submit_requirements" element={<SubmitRequirements />} />
                    <Route path="/documents/Privacy-Policy" element={<PrivacyPolicy />} />
                    <Route path="/thank-you" element={<ThankYou />} />
                </Routes>
            </main>
            <Footer />
            {!cookieConsent && <CookieConsent onConsent={() => setCookieConsent(true)} />}
        </div>
    );
};

const App = () => (
    <BrowserRouter>
        <AppContent />
    </BrowserRouter>
);

export default App;
